import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { terraProxyApiClient } from '../clients/testerClient';
import type { Company } from '../store/projectStore';
import { filterCompaniesForTenant } from '../utils/filter-targets-for-tenant';
import { useUserTenants } from './api/useUserTenants';
import { getCurrentTenantId } from '../store/tenantStorage';

const COMPANIES_QUERY_KEY = 'companies';
const COMPANIES_STALE_TIME_MS = 5 * 60 * 1000;
const COMPANIES_GC_TIME_MS = 30 * 60 * 1000;
const COMPANIES_ERROR_REFETCH_INTERVAL_MS = 5000;

export function useCompanies() {
  const {
    currentTenantId: authTenantId,
    isTerraUser,
    isFetched: areTenantsFetched,
  } = useUserTenants();
  const currentTenantId = authTenantId ?? getCurrentTenantId();
  const query = useQuery({
    queryKey: [COMPANIES_QUERY_KEY],
    queryFn: async (): Promise<Company[]> => {
      const result = await terraProxyApiClient.getAllCompanies();

      if (result.status === 200) {
        const companies = result.body.companies || [];

        return companies.map((company) => ({
          id: company.id,
          name: company.name,
          tenantId: company.tenantId || null,
        }));
      }

      throw new Error(`Failed to fetch companies: status ${result.status}`);
    },
    staleTime: COMPANIES_STALE_TIME_MS,
    gcTime: COMPANIES_GC_TIME_MS,
    refetchInterval: (currentQuery) =>
      currentQuery.state.status === 'error'
        ? COMPANIES_ERROR_REFETCH_INTERVAL_MS
        : false,
  });

  const companies = useMemo(() => {
    if (!areTenantsFetched) {
      return [];
    }

    return filterCompaniesForTenant({
      companies: query.data || [],
      tenantId: currentTenantId,
      restrictToCurrentTenant: isTerraUser,
    });
  }, [areTenantsFetched, query.data, currentTenantId, isTerraUser]);

  return {
    companies,
    isLoading: query.isLoading || !areTenantsFetched,
    error: query.error,
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
}
